import React from 'react'
import { useFlags } from 'shared/components/common/FlagsProvider'
import {
  Button,
  Grid,
  OldTileList as TileList,
  InputField,
  Hidden,
  FormSection,
  withClientOptions,
  Collapse,
  createStyles,
  withStyles
} from '@paintscout/ui'
import SearchIcon from '@material-ui/icons/Search'

import findIndex from 'lodash/findIndex'
import type { QuoteDocument, OptionsDocument } from 'paintscout'

import type { MuiTheme as Theme, WithStyles, WithClientOptions } from '@paintscout/ui'

import type { UpdateableItem, AreaLabelOption, LineItemOption, QuoteItemSection } from '@paintscout/util/builder'
import {
  getAreaLabelOptions,
  createArea,
  createLineItem,
  getLineItemOptions,
  getQuoteType,
  getQuoteTypeOptions,
  getObjectLabels,
  toUpdateableLineItem
} from '@paintscout/util/builder'

export interface ItemConfigurationProps extends WithStyles<typeof styles>, WithClientOptions {
  item?: UpdateableItem
  type?: any
  quoteType?: string
  quoteTypes?: string[]
  quote?: QuoteDocument
  section?: QuoteItemSection
  disableTypeChange?: boolean
  canDeactivate?: boolean
  onChange?: (item: any, type: any) => void
}

export interface ItemConfigurationState {
  itemTypeIndex?: number
  quoteTypeIndex?: number
  itemIndex?: number
  customItem?: boolean
  quoteType?: string
  item?: UpdateableItem
  itemTypeOptions: ItemTypeOption[]
  query?: string
}

export interface ItemTypeOption {
  label: string
  name: string
  itemOptions: (
    options: OptionsDocument,
    quote: QuoteDocument,
    quoteType: string
  ) => LineItemOption[] | AreaLabelOption[]
  configurationForm?: any
  createItem: (itemOption: any, quoteType: any) => UpdateableItem
}

const styles = (theme: Theme) => {
  return createStyles({
    root: {},
    lastSection: {},
    tileFormSection: {
      padding: '1.5rem 0'
    },
    searchGrid: {
      marginBottom: '0.5em'
    },
    searchField: {
      '&:focus-within $searchAdornment': {
        color: theme.paintscout.palette.primary + ' !important'
      }
    },
    searchAdornment: {
      width: 24,
      height: 24,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: theme.paintscout.palette.brand.Neutral.P80
    },
    customButton: {
      textTransform: 'none'
    }
  })
}

class ItemConfiguration extends React.PureComponent<ItemConfigurationProps, ItemConfigurationState> {
  public static getDerivedStateFromProps(props: ItemConfigurationProps, state: ItemConfigurationState) {
    const itemTypeIndex = props.type === null ? null : state.itemTypeIndex
    const itemIndex = props.item === null ? null : state.itemIndex
    const quoteTypeIndex = props.quoteType === null ? null : state.quoteTypeIndex

    const derivedState = {
      ...state,
      itemTypeIndex,
      itemIndex,
      quoteTypeIndex
    }

    return derivedState
  }

  constructor(props: ItemConfigurationProps) {
    super(props)

    const { options } = this.props.clientOptions
    const { quote, section } = this.props

    const itemTypeOptions = [
      {
        label: 'Area',
        name: 'area',
        itemOptions: (
          optionsArg: OptionsDocument,
          quoteArg: QuoteDocument,
          quoteTypeArg: string
        ): AreaLabelOption[] => {
          return getAreaLabelOptions({
            options: optionsArg,
            quote: quoteArg,
            quoteType: quoteTypeArg,
            canDeactivate: props.canDeactivate
          })
        },
        // configurationForm: AreaConfigurationForm,
        createItem: (itemOptionArg: any, quoteTypeArg: string): UpdateableItem => {
          const newArea = createArea({ quote, options, quoteType: quoteTypeArg })
          newArea.section = section
          if (itemOptionArg === null) {
            return newArea
          }

          return {
            ...newArea,
            label: itemOptionArg.label
          }
        }
      },
      {
        label: 'Line Item',
        name: 'lineItem',
        itemOptions: (optionsArg: OptionsDocument, quoteArg: QuoteDocument, quoteTypeArg: string): LineItemOption[] => {
          const lineItemOptions = getLineItemOptions({
            options: optionsArg,
            quote: quoteArg,
            quoteType: quoteTypeArg,
            rekeyMedia: true,
            canDeactivate: props.canDeactivate
          })

          return lineItemOptions.map((option) => ({
            ...option,
            calculateBy: option.calculateBy ?? 'hourlyRate'
          }))
        },
        createItem: (itemOptionArg: LineItemOption, quoteTypeArg: string): UpdateableItem => {
          if (itemOptionArg === null) {
            return createLineItem({ quoteType: quoteTypeArg, section, calculateBy: 'hourlyRate' })
          }
          return toUpdateableLineItem({ lineItemOption: itemOptionArg, quoteType: quoteTypeArg, section })
        }
      }
    ]

    const { item, quoteType, quoteTypes } = this.props

    const quoteTypeOptions = getQuoteTypeOptions({ options, quoteTypes, canDeactivate: props.canDeactivate })

    const type = item && item.type ? item.type : this.props.type
    let itemTypeIndex = type !== null ? findIndex(itemTypeOptions, { name: type }) : -1
    itemTypeIndex = itemTypeIndex === -1 ? null : itemTypeIndex

    let quoteTypeIndex = quoteType !== null ? findIndex(quoteTypeOptions, { key: quoteType }) : -1
    quoteTypeIndex = quoteTypeIndex === -1 ? null : quoteTypeIndex

    let itemIndex = -1
    if (itemTypeIndex !== null) {
      const itemOptions = itemTypeOptions[itemTypeIndex].itemOptions(options, quote, quoteType)
      const itemLabel = item && item.label ? item.label : null
      itemIndex = findIndex(itemOptions, { label: itemLabel as string })
    }
    itemIndex = itemIndex === -1 ? null : itemIndex

    const customItem = itemIndex === null && !!item

    this.state = {
      itemTypeIndex,
      itemIndex,
      quoteTypeIndex,
      customItem,
      item,
      itemTypeOptions,
      query: ''
    }
  }

  public render() {
    const { classes, quote, quoteTypes } = this.props
    const { itemTypeOptions, itemTypeIndex, itemIndex, customItem } = this.state
    const itemType = itemTypeIndex !== null ? itemTypeOptions[itemTypeIndex] : null
    const quoteType =
      quote.type === 'quoteTemplate' ? (quoteTypes.length === 1 ? quoteTypes[0] : 'all') : getQuoteType({ quote })

    return (
      <div className={classes.root}>
        {this.renderForm()}
        {this.renderItemTiles()}
        {(quoteType === 'all' || quoteTypes?.length > 1) && itemType && this.renderQuoteTypeTiles()}
        {!itemType && (
          <FormSection hideDivider={itemTypeIndex === null} classes={{ root: classes.tileFormSection }}>
            <TileList
              items={itemTypeOptions}
              selectedIndex={itemTypeIndex}
              allowCustom={false}
              title={'Item Type'}
              onChange={this.selectItemType}
              dense={itemIndex !== null || customItem}
              disabled={this.props.disableTypeChange}
            />
          </FormSection>
        )}
      </div>
    )
  }

  private renderItemTiles() {
    const { classes, quote, quoteTypes } = this.props
    const { itemTypeOptions, quoteTypeIndex, itemTypeIndex, itemIndex, customItem, query } = this.state
    const { options } = this.props.clientOptions
    const itemType = itemTypeIndex !== null ? itemTypeOptions[itemTypeIndex] : null

    const quoteTypeOptions = getQuoteTypeOptions({
      options,
      hideAll: true,
      quoteTypes,
      canDeactivate: this.props.canDeactivate
    })
    const quoteType = quoteTypeIndex !== null ? quoteTypeOptions[quoteTypeIndex].key : null
    const isQuoteTemplate = quote.type === 'quoteTemplate'
    const quoteTemplateType = isQuoteTemplate ? (quoteTypes.length === 1 ? quoteTypes[0] : 'all') : null
    const globalQuoteType = getQuoteType({ quote, quoteType: quoteTemplateType })
    const smartTemplate = quote?.smartTemplate && isQuoteTemplate

    const showItems =
      (globalQuoteType !== 'all' && (quoteTypes?.length ?? 1) < 2 && itemType !== null) ||
      (quoteTypeIndex !== null && itemType !== null)

    const itemOptions = itemType ? itemType.itemOptions(options, quote, quoteType ?? quoteTemplateType) : []

    const filteredItems = itemOptions.filter(
      (option) => option.label.toLowerCase().indexOf(query?.toLowerCase()) !== -1
    )

    return (
      <Collapse show={showItems}>
        <FormSection style={{ paddingTop: '1em' }} classes={{ root: classes.lastSection }} hideDivider={true}>
          <Grid className={classes.searchGrid} container={true} spacing={2} alignItems={'center'}>
            <Grid item={true} xs>
              <InputField
                id="query"
                className={classes.searchField}
                data-testid="item-search-input"
                placeholder={`Search existing ${itemType?.label ? itemType.label.toLowerCase() + 's' : ''}`}
                fullWidth={true}
                value={query}
                onChange={(ev) => this.setState({ query: ev.currentTarget.value })}
                startAdornment={
                  <div className={classes.searchAdornment}>
                    <SearchIcon />
                  </div>
                }
              />
            </Grid>
            {!smartTemplate && (
              <Grid item={true} xs={'auto'}>
                <Button
                  className={classes.customButton}
                  variant={'dialog'}
                  onClick={(event: any) => {
                    this.selectItem(event, null, true)
                  }}
                >
                  <Hidden smDown={true}>+ Custom {itemType?.label === 'Line Item' ? 'Item' : 'Area'}</Hidden>
                  <Hidden mdUp={true}>Custom</Hidden>
                </Button>
              </Grid>
            )}
          </Grid>
          <TileList
            items={query ? filteredItems : itemOptions}
            selectedIndex={itemIndex}
            allowCustom={false}
            customSelected={customItem}
            title={itemType ? itemType.label : ''}
            onChange={this.selectItem}
            dense={itemIndex !== null || customItem}
          />
        </FormSection>
      </Collapse>
    )
  }

  private renderQuoteTypeTiles() {
    const { classes, quoteTypes } = this.props
    const { itemTypeIndex, quoteTypeIndex } = this.state
    const { options } = this.props.clientOptions
    const quoteTypeOptions = getQuoteTypeOptions({
      options,
      hideAll: true,
      quoteTypes,
      canDeactivate: this.props.canDeactivate
    })
    const objectLabels = getObjectLabels({ options })

    return (
      <Collapse show={itemTypeIndex !== null}>
        <FormSection classes={{ root: classes.lastSection }} hideDivider={true}>
          <TileList
            items={quoteTypeOptions}
            selectedIndex={quoteTypeIndex}
            allowCustom={false}
            title={`${objectLabels.quote.value} Type`}
            onChange={this.selectQuoteType}
            dense={quoteTypeIndex !== null}
          />
        </FormSection>
      </Collapse>
    )
  }

  private renderForm() {
    const { classes } = this.props
    const { itemTypeOptions, itemTypeIndex, item } = this.state
    const itemType = itemTypeIndex !== null ? itemTypeOptions[itemTypeIndex] : null

    if (!item || !itemType || !itemType.configurationForm) {
      return null
    }

    return (
      <Collapse show={!!item}>
        <FormSection classes={{ root: classes.lastSection }} hideDivider={true}>
          <itemType.configurationForm item={item} onChange={this.handleFormChange} />
        </FormSection>
      </Collapse>
    )
  }

  private selectItemType = (event: any, itemTypeIndex: number) => {
    const newState: any = {
      itemTypeIndex
    }
    if (itemTypeIndex === null) {
      newState.itemIndex = null
      newState.item = null
    }
    this.setState(newState)

    const { itemTypeOptions } = this.state
    const type = itemTypeOptions[itemTypeIndex]

    if (this.props.onChange) {
      this.props.onChange(null, type)
    }
  }

  private selectItem = (event: any, itemIndex: number, custom: boolean) => {
    const { quote, quoteTypes } = this.props
    const { options } = this.props.clientOptions
    const { itemTypeOptions, quoteTypeIndex, query } = this.state
    const type = itemTypeOptions[this.state.itemTypeIndex]
    const quoteTypeOptions = getQuoteTypeOptions({
      options,
      hideAll: true,
      quoteTypes,
      canDeactivate: this.props.canDeactivate
    })
    const quoteType = quoteTypeIndex !== null ? quoteTypeOptions[quoteTypeIndex].key : null

    if (itemIndex === null && !custom) {
      this.setState({
        itemIndex,
        item: null,
        customItem: false
      })
      if (this.props.onChange) {
        this.props.onChange(null, type)
      }
      return
    }

    const { createItem } = type
    const quoteTemplateType = quote.type === 'quoteTemplate' ? (quoteTypes.length === 1 ? quoteTypes[0] : 'all') : null
    const itemOptions = type.itemOptions(options, quote, quoteType ?? quoteTemplateType)
    const filteredItems = itemOptions.filter(
      (option) => option.label.toLowerCase().indexOf(query?.toLowerCase()) !== -1
    )
    const itemOption = custom ? null : filteredItems[itemIndex]
    const newItem = {
      ...createItem(itemOption, quoteType ? quoteType : quoteTemplateType),
      label: custom ? query ?? '' : itemOption.label
    }

    this.setState({
      itemIndex,
      item: newItem,
      customItem: custom
    })

    if (this.props.onChange) {
      this.props.onChange(newItem, type)
    }
  }

  private selectQuoteType = (event: any, quoteTypeIndex: number) => {
    const { quote, quoteTypes } = this.props
    const { options } = this.props.clientOptions

    const { itemTypeOptions, itemTypeIndex } = this.state
    const type = itemTypeOptions[itemTypeIndex]
    const { createItem } = type

    let customItem = false
    let quoteType = null
    if (quoteTypeIndex !== null) {
      const quoteTypeOptions = getQuoteTypeOptions({
        options,
        hideAll: true,
        quoteTypes,
        canDeactivate: this.props.canDeactivate
      })
      quoteType = quoteTypeOptions[quoteTypeIndex].key

      const itemOptions = type.itemOptions(options, quote, quoteType)
      customItem = itemOptions.length === 0
    }

    this.setState({
      quoteTypeIndex,
      itemIndex: null,
      item: null,
      customItem
    })

    if (this.props.onChange && customItem) {
      const newItem = createItem(null, quoteType)
      this.props.onChange(newItem, type)
    }
  }

  private handleFormChange = (item: any) => {
    const { itemTypeOptions } = this.state
    const type = itemTypeOptions[this.state.itemTypeIndex]

    this.setState({ item })

    if (this.props.onChange) {
      this.props.onChange(item, type)
    }
  }

  private onFormChange = (item: any) => {
    const { itemTypeOptions } = this.state
    const type = itemTypeOptions[this.state.itemTypeIndex]

    this.setState({ item })

    if (this.props.onChange) {
      this.props.onChange(item, type)
    }
  }
}

const StyledItemConfiguration = withStyles(styles)(withClientOptions(ItemConfiguration))

function ItemConfigurationWithFlags(
  props: Omit<ItemConfigurationProps, 'canDeactivate' | 'classes' | 'clientOptions'>
) {
  const { canDeactivateSettingsItems } = useFlags()
  return <StyledItemConfiguration {...props} canDeactivate={!!canDeactivateSettingsItems} />
}

export default ItemConfigurationWithFlags
